import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  SkipForward, 
  Play, 
  Target, 
  MessageSquare, 
  Edit3, 
  Lightbulb, 
  BookOpen, 
  User, 
  CreditCard, 
  Zap,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  MousePointer
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const TutorialContainer = styled(motion.div)`
  width: 90vw;
  max-width: 600px;
  height: 80vh;
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  border-radius: 16px;
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const TutorialHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TutorialIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0A84FF, #7C3AED);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  font-weight: 600;
`;

const TutorialTitle = styled.h2`
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled(motion.button)`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#6a737d'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
`;

const TutorialContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const StepDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.active 
    ? '#0A84FF' 
    : props.completed 
      ? '#10B981' 
      : props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'
  };
  transition: all 0.3s ease;
`;

const StepContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StepIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.color || '#0A84FF'}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || '#0A84FF'};
  margin-bottom: 20px;
  font-size: 32px;
`;

const StepTitle = styled.h3`
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const StepDescription = styled.p`
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#6a737d'};
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 20px 0;
  max-width: 400px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 400px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'};
`;

const FeatureIcon = styled.div`
  color: ${props => props.color || '#0A84FF'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureText = styled.div`
  flex: 1;
  text-align: left;
`;

const FeatureName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  margin-bottom: 2px;
`;

const FeatureDesc = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#6a737d'};
`;

const TutorialFooter = styled.div`
  padding: 20px;
  border-top: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
`;

const SkipButton = styled(motion.button)`
  background: none;
  border: none;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#6a737d'};
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const NavButton = styled(motion.button)`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &.secondary {
    background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    border: 1px solid ${props => props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'};

    &:hover {
      background: ${props => props.theme.mode === 'dark' ? '#30363d' : '#e1e4e8'};
    }
  }

  &.primary {
    background: #0A84FF;
    color: white;

    &:hover {
      background: #005a9e;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TutorialModal = ({ isOpen, onClose, onComplete }) => {
  const { theme } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);

  const tutorialSteps = [
    {
      id: 'disclaimer',
      icon: <AlertTriangle size={32} />,
      iconColor: '#f59e0b',
      title: '중요 안내 및 주의 사항',
      description: 'AI가 작성하는 내용은 보조용입니다. 아래 사항을 반드시 확인하세요.',
      features: [
        { icon: <AlertTriangle size={16} />, name: '최신성 한계', desc: '최신 정보가 반영되지 않을 수 있습니다.', color: '#f59e0b' },
        { icon: <BookOpen size={16} />, name: '참고문헌 정확도', desc: '참고문헌/인용은 일부 틀릴 수 있으니 원문으로 검증하세요.', color: '#7c3aed' },
        { icon: <Lightbulb size={16} />, name: '사실성/추론 오류', desc: '사실과 다르거나 과장된 내용이 포함될 수 있습니다.', color: '#ef4444' },
        { icon: <Edit3 size={16} />, name: '저작권/표절 책임', desc: '인용·출처 표기 및 원문 확인은 사용자 책임입니다.', color: '#28a745' },
        { icon: <User size={16} />, name: '민감정보 입력 금지', desc: '개인정보, 기밀정보, 규제 대상 정보 입력을 피하세요.', color: '#10b981' },
        { icon: <MessageSquare size={16} />, name: '전문 자문 아님', desc: '법률/의학/재무 등 전문적 결정에 사용하지 마세요.', color: '#17a2b8' }
      ]
    }
  ];

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    onComplete && onComplete();
    onClose();
  };

  const handleSkip = () => {
    handleComplete();
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const currentStepData = tutorialSteps[currentStep];
  const isLastStep = currentStep === tutorialSteps.length - 1;

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <TutorialContainer
            theme={theme}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <TutorialHeader theme={theme}>
              <HeaderInfo>
                <TutorialIcon>
                  {currentStep + 1}
                </TutorialIcon>
                <TutorialTitle theme={theme}>
                  {currentStepData.title}
                </TutorialTitle>
              </HeaderInfo>
              <CloseButton
                theme={theme}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
              >
                <X size={18} />
              </CloseButton>
            </TutorialHeader>

            <TutorialContent theme={theme}>
              <StepIndicator>
                {tutorialSteps.map((_, index) => (
                  <StepDot
                    key={index}
                    active={index === currentStep}
                    completed={index < currentStep}
                    theme={theme}
                  />
                ))}
              </StepIndicator>

              <StepContent>
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <StepIcon color={currentStepData.iconColor}>
                    {currentStepData.icon}
                  </StepIcon>
                  
                  <StepTitle theme={theme}>
                    {currentStepData.title}
                  </StepTitle>
                  
                  <StepDescription theme={theme}>
                    {currentStepData.description}
                  </StepDescription>

                  <FeatureList>
                    {currentStepData.features.map((feature, index) => (
                      <FeatureItem key={index} theme={theme}>
                        <FeatureIcon color={feature.color}>
                          {feature.icon}
                        </FeatureIcon>
                        <FeatureText>
                          <FeatureName theme={theme}>{feature.name}</FeatureName>
                          <FeatureDesc theme={theme}>{feature.desc}</FeatureDesc>
                        </FeatureText>
                      </FeatureItem>
                    ))}
                  </FeatureList>
                </motion.div>
              </StepContent>
            </TutorialContent>

            <TutorialFooter theme={theme}>
              <SkipButton
                theme={theme}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSkip}
              >
                <SkipForward size={14} />
                건너뛰기
              </SkipButton>

              <NavigationButtons>
                <NavButton
                  className="secondary"
                  theme={theme}
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ChevronLeft size={14} />
                  이전
                </NavButton>

                <NavButton
                  className="primary"
                  onClick={handleNext}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isLastStep ? (
                    <>
                      <CheckCircle size={14} />
                      완료
                    </>
                  ) : (
                    <>
                      다음
                      <ChevronRight size={14} />
                    </>
                  )}
                </NavButton>
              </NavigationButtons>
            </TutorialFooter>
          </TutorialContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default TutorialModal;
